import { 
  signIn, 
  signUp, 
  signOut, 
  getCurrentUser, 
  confirmSignUp,
  resendSignUpCode,
  signInWithRedirect,
  fetchAuthSession  
} from 'aws-amplify/auth';

// ユーザー情報の型定義
export interface AuthUser {
  email: string;
  userId: string;
  isEmailVerified: boolean;
}

// 認証結果の型定義
interface AuthResult {
  success: boolean;
  error?: Error | unknown;
  result?: unknown;
}

// 認証状態チェック結果の型定義
interface AuthState {
  isAuthenticated: boolean;
  user?: AuthUser | null;
  error?: string;
}

// メール＋パスワードでサインイン
export const signInWithEmail = async (email: string, password: string): Promise<AuthResult> => {
  try {
    // 事前に現在の認証状態をチェック
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        console.log('メール認証: 既にサインイン済みです');
        const error = new Error('There is already a signed in user.');
        error.name = 'UserAlreadyAuthenticatedException';
        throw error;
      }
    } catch (authCheckError) {
      if ((authCheckError as Error).name !== 'UserAlreadyAuthenticatedException') {
        console.log('メール認証: 未認証状態を確認、認証処理を続行');
      } else {
        throw authCheckError;
      }
    }

    const result = await signIn({
      username: email,
      password,
    });
    return { success: true, result };
  } catch (error) {
    console.error('サインインエラー:', error);
    return { success: false, error };
  }
};

// メール＋パスワードでサインアップ
export const signUpWithEmail = async (email: string, password: string) => {
  try {
    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
      },
    });
    return { success: true, result };
  } catch (error) {
    console.error('サインアップエラー:', error);
    return { success: false, error };
  }
};

// メール認証コードの確認
export const confirmSignUpCode = async (email: string, code: string) => {
  try {
    const result = await confirmSignUp({
      username: email,
      confirmationCode: code,
    });
    return { success: true, result };
  } catch (error) {
    console.error('認証コード確認エラー:', error);
    return { success: false, error };
  }
};

// 認証コードの再送信
export const resendConfirmationCode = async (email: string) => {
  try {
    const result = await resendSignUpCode({
      username: email,
    });
    return { success: true, result };
  } catch (error) {
    console.error('認証コード再送信エラー:', error);
    return { success: false, error };
  }
};

// Googleでサインイン
export const signInWithGoogle = async () => {
  try {
    // 事前に現在の認証状態をチェック
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        console.log('Google認証: 既にサインイン済みです');
        const error = new Error('There is already a signed in user.');
        error.name = 'UserAlreadyAuthenticatedException';
        throw error;
      }
    } catch (authCheckError) {
      if ((authCheckError as Error).name !== 'UserAlreadyAuthenticatedException') {
        console.log('Google認証: 未認証状態を確認、認証処理を続行');
      } else {
        throw authCheckError;
      }
    }

    // 現在のURLを確認してデバッグ情報を出力
    console.log('Google認証開始:', {
      currentUrl: window.location.href,
      origin: window.location.origin,
      userAgent: navigator.userAgent,
    });
    
    // 認証フローの種類を保存（リダイレクト後の復元用）
    saveAuthFlow('google');
    
    console.log('Google認証: signInWithRedirect実行中...');
    await signInWithRedirect({
      provider: 'Google',
    });
    
    console.log('Google認証: signInWithRedirect完了（リダイレクト前）');
  } catch (error) {
    console.error('Google サインインエラー:', error);
    
    // エラーの詳細情報を出力
    const err = error as Error;
    console.error('エラー詳細:', {
      name: err.name,
      message: err.message,
      stack: err.stack,
      currentUrl: window.location.href,
    });
    
    // より詳細なエラー情報をログ出力
    if (err.name === 'AuthUserPoolException') {
      console.error('Amplify設定エラー: UserPoolが正しく設定されていません');
      console.error('amplifyconfiguration.jsonの内容を確認してください');
    } else if (err.name === 'InvalidOriginException') {
      console.error('OAuth設定エラー: リダイレクトURLが一致しません');
      console.error('AWS Cognitoの設定で以下のURLを許可してください:', window.location.origin);
    }
    
    throw error;
  }
};

// サインアウト
export const signOutUser = async () => {
  try {
    await signOut();
    clearAuthFlow();
    return { success: true };
  } catch (error) {
    console.error('サインアウトエラー:', error);
    return { success: false, error };
  }
};

// 現在のユーザー情報を取得
export const getCurrentUserInfo = async (): Promise<AuthUser | null> => {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    
    console.log('ユーザー情報（デバッグ）:', {
      user,
      session: session.tokens?.idToken?.payload,
      userAttributes: user.signInDetails,
    });
    
    // 複数のソースからメールアドレスを取得を試行
    let email = '';
    
    // 1. signInDetails.loginId から取得（従来の方法）
    if (user.signInDetails?.loginId) {
      email = user.signInDetails.loginId;
    }
    // 2. IDトークンのペイロードから取得（GoogleSSO等）
    else if (session.tokens?.idToken?.payload?.email) {
      email = session.tokens.idToken.payload.email as string;
    }
    // 3. username から取得（メールアドレスがusernameの場合）
    else if (user.username && user.username.includes('@')) {
      email = user.username;
    }
    
    if (!email) {
      console.error('メールアドレスが見つかりません。ユーザー情報:', user);
      return null;
    }
    
    return {
      email,
      userId: user.userId,
      isEmailVerified: session.tokens?.idToken?.payload?.email_verified === true || 
                      !!user.signInDetails?.loginId,
    };
  } catch (error) {
    console.error('ユーザー情報取得エラー:', error);
    return null;
  }
};

// 認証状態を確認
export const checkAuthState = async (): Promise<AuthState> => {
  try {
    console.log('checkAuthState: getCurrentUser実行');
    const user = await getCurrentUser();
    console.log('checkAuthState: getCurrentUser成功', user);
    
    const userInfo = await getCurrentUserInfo();
    console.log('checkAuthState: getCurrentUserInfo成功', userInfo);
    
    return {
      isAuthenticated: true,
      user: userInfo,
    };
  } catch (error) {
    console.log('認証チェック: 未認証状態', error);
    
    // エラーの詳細をログ出力
    if (error instanceof Error) {
      console.log('認証エラーの詳細:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    }
    
    return {
      isAuthenticated: false,
      error: (error as Error)?.message,
    };
  }
};

// デバッグ用：ユーザー情報と設定を確認
export const debugUserInfo = async () => {
  try {
    console.log('=== 認証デバッグ情報 ===');
    console.log('Current URL:', window.location.href);
    console.log('User Agent:', navigator.userAgent);
    console.log('Environment:', process.env.NODE_ENV);
    
    // Amplify設定状況の確認
    try {
      const { Amplify } = await import('aws-amplify');
      const currentConfig = Amplify.getConfig();
      console.log('Amplify Config Auth:', currentConfig.Auth);
    } catch (configError) {
      console.log('Amplify Config Error:', configError);
    }
    
    // 認証状態の確認
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      
      console.log('Current User:', user);
      console.log('Auth Session:', session);
      console.log('ID Token Payload:', session.tokens?.idToken?.payload);
    } catch (userError) {
      console.log('User Error:', userError);
    }
    
    // LocalStorageのAmplify関連データを確認
    const amplifyKeys = Object.keys(localStorage).filter(key => 
      key.includes('Cognito') || key.includes('amplify')
    );
    console.log('Amplify LocalStorage Keys:', amplifyKeys);
    amplifyKeys.forEach(key => {
      console.log(`${key}:`, localStorage.getItem(key));
    });
    
    // セッションストレージの確認
    const sessionKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('auth') || key.includes('cognito') || key.includes('amplify')
    );
    console.log('Auth SessionStorage Keys:', sessionKeys);
    sessionKeys.forEach(key => {
      console.log(`${key}:`, sessionStorage.getItem(key));
    });
    
    // URLパラメータの確認
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString()) {
      console.log('URL Parameters:', urlParams.toString());
    }
    
    console.log('=== デバッグ情報終了 ===');
  } catch (error) {
    console.error('デバッグ情報取得エラー:', error);
  }
};

// OAuth認証結果の型定義
interface OAuthCallbackResult {
  success: boolean;
  isOAuthCallback: boolean;
  error?: Error;
  errorType?: string;
  errorDescription?: string;
}

// OAuth認証完了の検出
export const handleOAuthCallback = async (): Promise<OAuthCallbackResult> => {
  try {
    // SSR時は処理をスキップ
    if (typeof window === 'undefined') {
      return { success: false, isOAuthCallback: false };
    }
    
    // URLにOAuth認証の結果パラメータが含まれているかチェック
    const urlParams = new URLSearchParams(window.location.search);
    const hasOAuthParams = urlParams.has('code') || urlParams.has('state') || 
                          urlParams.has('error') || urlParams.has('error_description');
    
    if (hasOAuthParams) {
      console.log('OAuth認証コールバックを検出:', urlParams.toString());
      
      // エラーパラメータの確認
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      if (error) {
        console.error('OAuth認証エラー:', { error, errorDescription });
        
        // URLパラメータをクリア
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // エラーの種類に応じて詳細なメッセージを返す
        let errorMessage = 'OAuth認証に失敗しました。';
        
        switch (error) {
          case 'invalid_request':
            if (errorDescription?.includes('invalid_scope')) {
              errorMessage = 'OAuth設定が更新されました。もう一度Google認証をお試しください。';
            } else {
              errorMessage = 'OAuth認証リクエストが無効です。';
            }
            break;
          case 'access_denied':
            errorMessage = 'OAuth認証がキャンセルされました。';
            break;
          case 'unsupported_response_type':
            errorMessage = 'OAuth設定エラー: サポートされていない応答タイプです。';
            break;
          default:
            errorMessage = `OAuth認証エラー: ${error}`;
        }
        
        return { 
          success: false, 
          isOAuthCallback: true, 
          error: new Error(errorMessage),
          errorType: error,
          errorDescription: errorDescription || undefined
        };
      }
      
      // 認証状態を確認（エラーがない場合）
      const authState = await checkAuthState();
      if (authState.isAuthenticated) {
        console.log('OAuth認証が完了しました');
        // URLパラメータをクリア
        window.history.replaceState({}, document.title, window.location.pathname);
        return { success: true, isOAuthCallback: true };
      }
    }
    
    return { success: false, isOAuthCallback: false };
  } catch (error) {
    console.error('OAuth認証コールバック処理エラー:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error(String(error)), 
      isOAuthCallback: true 
    };
  }
};

// セッションストレージを使った認証状態の保存/復元
export const saveAuthFlow = (flowType: 'google' | 'email') => {
  sessionStorage.setItem('authFlow', flowType);
  sessionStorage.setItem('authFlowTimestamp', Date.now().toString());
};

export const getAuthFlow = () => {
  const flowType = sessionStorage.getItem('authFlow');
  const timestamp = sessionStorage.getItem('authFlowTimestamp');
  
  // 10分以内の認証フローのみ有効とする
  if (flowType && timestamp) {
    const age = Date.now() - parseInt(timestamp);
    if (age < 10 * 60 * 1000) { // 10分
      return flowType as 'google' | 'email';
    }
  }
  
  return null;
};

export const clearAuthFlow = () => {
  sessionStorage.removeItem('authFlow');
  sessionStorage.removeItem('authFlowTimestamp');
};

// 認証状態を完全にリセット
export const resetAuthState = async () => {
  try {
    // 現在のユーザーを取得してサインアウト
    const currentUser = await getCurrentUser();
    if (currentUser) {
      await signOut();
    }
  } catch (error) {
    console.log('認証状態リセット: 既にサインアウト済みまたはエラー', error);
  }
  
  // ローカルストレージとセッションストレージをクリア
  if (typeof window !== 'undefined') {
    localStorage.clear();
    sessionStorage.clear();
  }
  
  console.log('認証状態をリセットしました');
};

// ウィンドウ間通信で認証情報を共有
export const shareAuthWithNewWindow = (targetWindow: Window) => {
  try {
    // 現在の認証情報を取得
    const currentClientId = process.env.NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID;
    if (!currentClientId) return;

    const lastAuthUserKey = `CognitoIdentityServiceProvider.${currentClientId}.LastAuthUser`;
    const lastAuthUser = localStorage.getItem(lastAuthUserKey);
    
    if (lastAuthUser) {
      // 認証情報を新しいウィンドウに送信
      const authData = {
        type: 'AUTH_SHARE',
        clientId: currentClientId,
        lastAuthUser: lastAuthUser,
        tokens: {}
      };

      // トークン情報も含める
      const accessTokenKey = `CognitoIdentityServiceProvider.${currentClientId}.${lastAuthUser}.accessToken`;
      const idTokenKey = `CognitoIdentityServiceProvider.${currentClientId}.${lastAuthUser}.idToken`;
      const refreshTokenKey = `CognitoIdentityServiceProvider.${currentClientId}.${lastAuthUser}.refreshToken`;
      
      authData.tokens = {
        accessToken: localStorage.getItem(accessTokenKey),
        idToken: localStorage.getItem(idTokenKey),
        refreshToken: localStorage.getItem(refreshTokenKey)
      };

      targetWindow.postMessage(authData, window.location.origin);
      console.log('認証情報を新しいウィンドウに送信:', authData);
    }
  } catch (error) {
    console.error('認証情報の共有エラー:', error);
  }
};

// 認証情報を受信して設定
export const receiveAuthFromParent = () => {
  return new Promise((resolve) => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'AUTH_SHARE') {
        try {
          const { clientId, lastAuthUser, tokens } = event.data;
          
          // ローカルストレージに認証情報を設定
          const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
          localStorage.setItem(lastAuthUserKey, lastAuthUser);
          
          // トークン情報を設定
          Object.entries(tokens).forEach(([key, value]) => {
            if (value) {
              const tokenKey = `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.${key}`;
              localStorage.setItem(tokenKey, value as string);
            }
          });
          
          console.log('親ウィンドウから認証情報を受信:', event.data);
          window.removeEventListener('message', handleMessage);
          resolve(true);
        } catch (error) {
          console.error('認証情報の受信エラー:', error);
          resolve(false);
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // タイムアウト設定
    setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      resolve(false);
    }, 5000);
  });
};